import { useEffect, useState, useRef } from "react";
import _ from "lodash";
import moment from "moment";
import useI18n from "Hooks/UseI18n";

const MS_UNITS = {
  SECOND: 1000,
  MINUTE: 60 * 1000,
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
  WEEK: 7 * 24 * 60 * 60 * 1000,
  // MONTH is variable, handled by moment
};

const ACTIVE_TIME_UNIT = {
  EXPIRED: "expired",
  MONTHS: "months",
  WEEKS: "weeks",
  DAYS: "days",
  HOURS: "hours",
  MINUTES: "minutes",
  SECONDS: "seconds",
};

const calculateTimeDiffDetails = ({ t, expiryTimestamp }) => {
  const now = moment();
  const expiry = moment(expiryTimestamp);
  const diffMs = expiry.diff(now);

  if (diffMs <= 0) {
    return {
      text: t("common:closed"),
      nextUpdateIn: 0,
      isExpired: true,
      activeTimeUnit: ACTIVE_TIME_UNIT.EXPIRED,
    };
  }

  const diffs = getDetailedDiffs(now, expiry);

  if (!diffs) {
    return {
      text: t("common:closed"),
      nextUpdateIn: 0,
      isExpired: true,
      activeTimeUnit: ACTIVE_TIME_UNIT.EXPIRED,
    };
  }

  for (const formatter of timeFormatters) {
    if (formatter.condition(diffs)) {
      const text = formatter.getText({ t, diffs }).trim();
      let nextUpdateIn = formatter.getNextUpdate(now, expiry, diffMs, diffs);
      // Ensure nextUpdateIn is at least 1ms (or more realistically 1000ms for seconds)
      // and not greater than remaining diffMs
      nextUpdateIn = Math.max(MS_UNITS.SECOND, Math.min(nextUpdateIn, diffMs));
      // If the calculated nextUpdateIn is 0 but not expired, default to 1 second
      if (nextUpdateIn <= 0 && diffMs > 0) nextUpdateIn = MS_UNITS.SECOND;

      return {
        text,
        nextUpdateIn,
        isExpired: false,
        activeTimeUnit: formatter.name,
      };
    }
  }

  // fallback if no formatter matched
  const countSecond = Math.floor(diffMs / MS_UNITS.SECOND);
  return {
    text: t("common:count_second", {
      count: countSecond,
    }),
    nextUpdateIn: MS_UNITS.SECOND,
    isExpired: false,
    activeTimeUnit: ACTIVE_TIME_UNIT.SECONDS,
  };
};

// Helper to calculate all relevant time differences
const getDetailedDiffs = (now, expiry) => {
  if (expiry.isBefore(now)) {
    return null; // Indicates expired
  }
  const totalMonths = expiry.diff(now, "months");
  const nowPlusMonths = now.clone().add(totalMonths, "months");
  const remainingWeeks = expiry.diff(nowPlusMonths, "weeks");
  const nowPlusMonthsAndWeeks = nowPlusMonths
    .clone()
    .add(remainingWeeks, "weeks");
  const remainingDays = expiry.diff(nowPlusMonthsAndWeeks, "days");
  const nowPlusMonthsWeeksDays = nowPlusMonthsAndWeeks
    .clone()
    .add(remainingDays, "days");
  const remainingHours = expiry.diff(nowPlusMonthsWeeksDays, "hours");
  const nowPlusMonthsWeeksDaysHours = nowPlusMonthsWeeksDays
    .clone()
    .add(remainingHours, "hours");
  const remainingMinutes = expiry.diff(nowPlusMonthsWeeksDaysHours, "minutes");
  const nowPlusMonthsWeeksDaysHoursMinutes = nowPlusMonthsWeeksDaysHours
    .clone()
    .add(remainingMinutes, "minutes");
  const remainingSeconds = expiry.diff(
    nowPlusMonthsWeeksDaysHoursMinutes,
    "seconds"
  );

  return {
    totalMonths,
    remainingWeeks,
    remainingDays,
    remainingHours,
    remainingMinutes,
    remainingSeconds,
  };
};

const timeFormatters = [
  {
    name: ACTIVE_TIME_UNIT.MONTHS,
    condition: diffs => diffs.totalMonths > 0,
    getText: ({ t, diffs }) =>
      `${t("common:count_month", {
        count: diffs.totalMonths,
      })} ${
        diffs.remainingWeeks > 0
          ? `${t("common:count_week", {
              count: diffs.remainingWeeks,
            })}`
          : ""
      }`,
    getNextUpdate: (now, expiry, diffMs, diffs) => {
      const nextMonthStart = now.clone().add(1, "month").startOf("month");
      const nextWeekStart = now.clone().add(1, "week").startOf("isoWeek");
      let updateIn = Math.min(
        nextMonthStart.diff(now),
        nextWeekStart.diff(now)
      );
      if (diffs.totalMonths === 0 && diffs.remainingWeeks < 1) {
        updateIn = Math.min(updateIn, MS_UNITS.DAY);
      }
      if (diffMs < MS_UNITS.DAY) return MS_UNITS.HOUR;
      if (diffMs < MS_UNITS.HOUR) return MS_UNITS.MINUTE;
      return updateIn;
    },
  },
  {
    name: ACTIVE_TIME_UNIT.WEEKS,
    condition: diffs => diffs.remainingWeeks > 0,
    getText: ({ t, diffs }) =>
      `${t("common:count_week", {
        count: diffs.remainingWeeks,
      })} ${
        diffs.remainingDays > 0
          ? `${t("common:count_day", {
              count: diffs.remainingDays,
            })}`
          : ""
      }`,
    getNextUpdate: (now, expiry, diffMs) => {
      const nextWeekStart = now.clone().add(1, "week").startOf("isoWeek");
      const nextDayStart = now.clone().add(1, "day").startOf("day");
      const updateIn = Math.min(
        nextWeekStart.diff(now),
        nextDayStart.diff(now)
      );
      if (diffMs < MS_UNITS.DAY) return MS_UNITS.HOUR;
      if (diffMs < MS_UNITS.HOUR) return MS_UNITS.MINUTE;
      return updateIn;
    },
  },
  {
    name: ACTIVE_TIME_UNIT.DAYS,
    condition: diffs => diffs.remainingDays > 0,
    getText: ({ t, diffs }) =>
      `${t("common:count_day", {
        count: diffs.remainingDays,
      })} ${
        diffs.remainingHours > 0
          ? `${t("common:count_hour", {
              count: diffs.remainingHours,
            })}`
          : ""
      }`,
    getNextUpdate: (now, expiry, diffMs) => {
      const nextDayStart = now.clone().add(1, "day").startOf("day");
      const nextHourStart = now.clone().add(1, "hour").startOf("hour");
      const updateIn = Math.min(
        nextDayStart.diff(now),
        nextHourStart.diff(now)
      );
      if (diffMs < MS_UNITS.HOUR) return MS_UNITS.MINUTE;
      if (diffMs < MS_UNITS.MINUTE) return MS_UNITS.SECOND;
      return updateIn;
    },
  },
  {
    name: ACTIVE_TIME_UNIT.HOURS,
    condition: diffs => diffs.remainingHours > 0,
    getText: ({ t, diffs }) =>
      `${t("common:count_hour", {
        count: diffs.remainingHours,
      })} ${
        diffs.remainingMinutes > 0
          ? `${t("common:count_minute", {
              count: diffs.remainingMinutes,
            })}`
          : ""
      }`,
    getNextUpdate: (now, expiry, diffMs) => {
      const nextHourStart = now.clone().add(1, "hour").startOf("hour");
      const nextMinuteStart = now.clone().add(1, "minute").startOf("minute");
      const updateIn = Math.min(
        nextHourStart.diff(now),
        nextMinuteStart.diff(now)
      );
      if (diffMs < MS_UNITS.MINUTE) return MS_UNITS.SECOND;
      return updateIn;
    },
  },
  {
    name: ACTIVE_TIME_UNIT.MINUTES,
    condition: diffs => diffs.remainingMinutes > 0,
    getText: ({ t, diffs }) =>
      `${t("common:count_minute", {
        count: diffs.remainingMinutes,
      })} ${
        diffs.remainingSeconds > 0
          ? `${t("common:count_second", {
              count: diffs.remainingSeconds,
            })}`
          : ""
      }`,
    getNextUpdate: (now, expiry, diffMs) => {
      const nextMinuteStart = now.clone().add(1, "minute").startOf("minute");
      if (diffMs < MS_UNITS.MINUTE) return MS_UNITS.SECOND;
      return Math.min(nextMinuteStart.diff(now), MS_UNITS.SECOND * 10);
    },
  },
  {
    name: ACTIVE_TIME_UNIT.SECONDS,
    condition: diffs => diffs.remainingSeconds >= 0,
    getText: ({ t, diffs }) =>
      `${t("common:count_second", {
        count: diffs.remainingSeconds,
      })}`,
    getNextUpdate: () => MS_UNITS.SECOND,
  },
];

const useExpiryCountdown = (expiryTimestamp, onExpire) => {
  const { t } = useI18n(["common"]);
  const [countdownDetails, setCountdownDetails] = useState(() =>
    calculateTimeDiffDetails({
      t,
      expiryTimestamp,
    })
  );
  const timeoutRef = useRef(null);
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    const initialDetails = calculateTimeDiffDetails({
      t,
      expiryTimestamp,
    });
    setCountdownDetails(initialDetails);

    if (initialDetails.isExpired) {
      // do nothing if already expired
      return;
    }

    const scheduleUpdate = () => {
      const details = calculateTimeDiffDetails({
        t,
        expiryTimestamp,
      });
      setCountdownDetails(details);

      if (details.isExpired) {
        if (_.isFunction(onExpireRef.current)) {
          onExpireRef.current();
        }
        return;
      }

      let updateInterval = details.nextUpdateIn;
      const remainingMs = expiryTimestamp - Date.now();

      if (
        details.activeTimeUnit === ACTIVE_TIME_UNIT.SECONDS ||
        details.activeTimeUnit === ACTIVE_TIME_UNIT.MINUTES
      ) {
        updateInterval = Math.max(MS_UNITS.SECOND, updateInterval);
      } else {
        updateInterval = Math.max(MS_UNITS.MINUTE / 6, updateInterval); // e.g., 10 seconds
      }

      updateInterval = Math.min(updateInterval, remainingMs);
      updateInterval = Math.max(MS_UNITS.SECOND, updateInterval);

      if (remainingMs <= 0) {
        setCountdownDetails({
          text: t("common:closed"),
          nextUpdateIn: 0,
          isExpired: true,
          activeTimeUnit: ACTIVE_TIME_UNIT.EXPIRED,
        });
        if (_.isFunction(onExpireRef.current)) {
          onExpireRef.current();
        }
        return;
      }

      timeoutRef.current = setTimeout(scheduleUpdate, updateInterval);
    };

    let firstInterval = initialDetails.nextUpdateIn;
    const initialRemainingMs = expiryTimestamp - Date.now();

    if (
      initialDetails.activeTimeUnit === ACTIVE_TIME_UNIT.SECONDS ||
      initialDetails.activeTimeUnit === ACTIVE_TIME_UNIT.MINUTES
    ) {
      firstInterval = Math.max(MS_UNITS.SECOND, firstInterval);
    } else {
      firstInterval = Math.max(MS_UNITS.MINUTE / 6, firstInterval);
    }

    firstInterval = Math.min(firstInterval, initialRemainingMs);
    firstInterval = Math.max(MS_UNITS.SECOND, firstInterval);

    if (initialRemainingMs <= 0) {
      if (_.isFunction(onExpireRef.current)) {
        onExpireRef.current();
      }
      return;
    }

    timeoutRef.current = setTimeout(scheduleUpdate, firstInterval);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [expiryTimestamp]); // t value change every render we cant add t here in dependency array

  return {
    text: countdownDetails.text,
    isExpired: countdownDetails.isExpired,
  };
};

export default useExpiryCountdown;
